import React, { useState } from "react";
import axios from "axios";
import { 
  FaDna, FaClipboard, FaSpinner, FaListAlt, FaCog, FaHighlighter, 
  FaBullseye, FaFlask, FaArrowLeft 
} from "react-icons/fa";
import "../css/PrimerComponent.css";

const Primer3PlusPage = () => {
  const [sequence, setSequence] = useState("");
  const [seqID, setSeqID] = useState("CustomSample");
  const [activeTab, setActiveTab] = useState("main");
  const [results, setResults] = useState([]);
  const [summary, setSummary] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showSummary, setShowSummary] = useState(false);
  const [showInput, setShowInput] = useState(true);
  const [task, setTask] = useState("generic"); // Primer task

  // Main Settings
  const [mainParams, setMainParams] = useState({
    num_return: 5,
    pick_left: 1,
    pick_right: 1,
    pick_internal: 0,
    product_range: "150-250 100-300 301-400 401-500 501-600 601-700 701-850 851-1000",
  });

  // General Settings
  const [generalParams, setGeneralParams] = useState({
    min_size: 18,
    opt_size: 20,
    max_size: 23,
    min_tm: 57,
    opt_tm: 59,
    max_tm: 62,
    max_tm_diff: 5,
    min_gc: 30,
    opt_gc: 50,
    max_gc: 70,
    max_poly_x: 4,
    gc_clamp: 0,
    dna_conc: 50,
    na_conc: 50,
    mg_conc: 1.5,
    dntp_conc: 0.6,
    max_end_stability: 9.0,
  });

  // Internal Oligo Settings
  const [internalOligoParams, setInternalOligoParams] = useState({
    io_min_tm: 57,
    io_opt_tm: 60,
    io_max_tm: 63,
    io_min_size: 18,
    io_opt_size: 20,
    io_max_size: 27,
    io_min_gc: 20,
    io_opt_gc: 50,
    io_max_gc: 80,
    io_max_self_any_th: 47,
    io_max_self_end_th: 47,
    io_max_hairpin_th: 47,
    io_max_self_any: 12,
    io_max_self_end: 12,
    io_max_poly_x: 5,
    io_max_library_mishybrid: 12,
  });

  // Mispriming / Penalties
  const [misprimeParams, setMisprimeParams] = useState({
    max_self_any: 8,
    max_self_end: 3,
    max_template_mispriming: 12,
    pair_max_template_mispriming: 24,
  });

  // Advanced / Sequence regions
  const [advancedParams, setAdvancedParams] = useState({
    left_overhang: "",
    right_overhang: "",
  });
  const [regions, setRegions] = useState({
    target: "",
    excluded: "",
    included_region: "",
  });

  const handleParamChange = (e, setter) => {
    const { name, value } = e.target;
    setter(prev => ({
      ...prev,
      [name]: isNaN(value) || name.includes("range") ? value : Number(value),
    }));
  };

  const cleanSequence = (seq) => seq.toUpperCase().replace(/[^ATGC]/g, "");
  const formatNumber = (val, decimals = 2) =>
    val != null && !isNaN(val) ? Number(val).toFixed(decimals) : "N/A";
  const calcStop = (start, len) =>
    typeof start === "number" && typeof len === "number" ? start + len - 1 : "N/A";
  const copyToClipboard = (text) => navigator.clipboard.writeText(text);

  const determinePrimerTask = () => {
    if (mainParams.pick_internal) return "pick_internal_oligo";
    if (mainParams.pick_left && mainParams.pick_right) return "generic";
    if (mainParams.pick_left && !mainParams.pick_right) return "pick_left_only";
    if (!mainParams.pick_left && mainParams.pick_right) return "pick_right_only";
    return "generic";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    setResults([]);
    setSummary("");
    setShowInput(false);

    try {
      const cleanedSeq = cleanSequence(sequence);
      if (!cleanedSeq) {
        setError("Sequence contains no valid ATGC bases.");
        setLoading(false);
        setShowInput(true);
        return;
      }

      let payload = {
        sequence: cleanedSeq,
        sequence_id: seqID,
        primer_task: task,
        primer_pick_left_primer: mainParams.pick_left,
        primer_pick_right_primer: mainParams.pick_right,
        primer_pick_internal_oligo: mainParams.pick_internal,
        primer_num_return: mainParams.num_return,
        primer_product_size_range: mainParams.product_range,
        ...generalParams,
        ...misprimeParams,
        ...advancedParams,
        ...regions,
      };
      if (mainParams.pick_internal) {
        payload = { ...payload, ...internalOligoParams };
      }

      const res = await axios.post("http://127.0.0.1:8000/api/primer3/", payload);
      setResults(res.data.primer_pairs || []);
      setSummary(res.data.summary || "");
    } catch (err) {
      setError("Error running Primer3: " + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  const renderSequence = () => {
    const seq = cleanSequence(sequence);
    if (!seq) return null;
    const lines = [];
    for (let i = 0; i < seq.length; i += 50) {
      lines.push(
        <div key={i} className="sequence-line">
          <span className="seq-index">{i + 1}</span>{" "}
          {seq.slice(i, i + 50).match(/.{1,10}/g).join("  ")}
        </div>
      );
    }
    return <pre className="sequence-block">{lines}</pre>;
  };

  const renderTab = () => {
    const paramMap = {
      main: mainParams,
      general: generalParams,
      internal: internalOligoParams,
      misprime: misprimeParams,
      regions: regions,
      advanced: advancedParams,
    };
    const setterMap = {
      main: setMainParams,
      general: setGeneralParams,
      internal: setInternalOligoParams,
      misprime: setMisprimeParams,
      regions: setRegions,
      advanced: setAdvancedParams,
    };

    const icons = {
      main: <FaListAlt />,
      general: <FaCog />,
      internal: <FaHighlighter />,
      misprime: <FaBullseye />,
      regions: <FaFlask />,
      advanced: <FaHighlighter />,
    };

    const labels = {
      main: "Main Settings",
      general: "General Settings",
      internal: "Internal Oligo",
      misprime: "Penalties",
      regions: "Advanced Seq.",
      advanced: "Advanced Settings",
    };

    return (
      <div className="tab-section">
        <h3>{icons[activeTab]} {labels[activeTab]}</h3>
        <div className="params-grid">
          {Object.entries(paramMap[activeTab]).map(([key, val]) => (
            <label key={key}>
              {key.replace(/_/g, " ")}:
              {typeof val === "number" ? (
                <input type="number" name={key} value={val} onChange={(e) => handleParamChange(e, setterMap[activeTab])} />
              ) : (
                <input type="text" name={key} value={val} onChange={(e) => handleParamChange(e, setterMap[activeTab])} />
              )}
            </label>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="primer-wrapper">
      <div className="primer-header">
        <FaDna className="dna-icon" />
        <h1>Primer3Plus Designer</h1>
        <p>Pick primers from a DNA sequence</p>
      </div>

      {showInput ? (
        <form onSubmit={handleSubmit} className="primer-form">
          <div className="task-selection">
            <label>Task:
              <select value={task} onChange={(e) => setTask(e.target.value)}>
                <option value="generic">generic</option>
                <option value="pick_left_only">pick_left_only</option>
                <option value="pick_right_only">pick_right_only</option>
                <option value="pick_internal_oligo">pick_internal_oligo</option>
              </select>
            </label>
          </div>
          <textarea placeholder="Paste DNA sequence here..." value={sequence} onChange={(e) => setSequence(e.target.value)} required />
          {renderSequence()}

          <div className="tabs">
            {["main","general","internal","misprime","regions","advanced"].map(tab => (
              <button
                type="button"
                key={tab}
                className={`tab-btn ${activeTab === tab ? "active" : ""}`}
                onClick={() => setActiveTab(tab)}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>
          {renderTab()}

          <button type="submit" className="run-btn" disabled={loading}>
            {loading ? <FaSpinner className="spin" /> : "Run Primer3"}
          </button>
          {error && <p className="error">{error}</p>}
        </form>
      ) : (
        <div className="status-section">
          {loading ? (
            <div className="loading"><FaSpinner className="spin big" /> Running Primer3...</div>
          ) : (
            <div className="primer-results">
              <button className="back-btn" onClick={() => setShowInput(true)}><FaArrowLeft /> Back</button>
              <h2>Primer3Plus Results</h2>
              <button className="summary-toggle" onClick={() => setShowSummary(!showSummary)}>
                {showSummary ? "Hide Summary" : "Show Summary"}
              </button>
              {showSummary && <pre className="summary-block">{summary}</pre>}

              <div className="results-grid">
                {results.map((pair, i) => (
                  <div className="primer-card" key={i}>
                    <h4>Primer Pair {i + 1}</h4>
                    <table>
                      <thead>
                        <tr>
                          <th>Type</th><th>Sequence</th><th>Length</th>
                          <th>Start</th><th>Stop</th><th>Tm</th><th>GC%</th>
                          <th>Any</th><th>End</th><th>TB</th><th>HP</th><th>3' Stab</th><th>Penalty</th>
                        </tr>
                      </thead>
                      <tbody>
                        {["left","right"].map(side => (
                          <tr key={side}>
                            <td>{side === "left" ? "Forward" : "Reverse"}</td>
                            <td className="mono">
                              {pair[`${side}_sequence`]}
                              <FaClipboard className="copy" onClick={() => copyToClipboard(pair[`${side}_sequence`])} />
                            </td>
                            <td>{pair[`${side}_sequence`].length}</td>
                            <td>{pair[`${side}_start`]}</td>
                            <td>{calcStop(pair[`${side}_start`], pair[`${side}_sequence`].length)}</td>
                            <td>{formatNumber(pair[`${side}_tm`], 2)}</td>
                            <td>{formatNumber(pair[`${side}_gc`], 2)}</td>
                            <td>{formatNumber(pair[`${side}_self_any`], 1)}</td>
                            <td>{formatNumber(pair[`${side}_self_end`], 1)}</td>
                            <td>{formatNumber(pair[`${side}_tb`], 1)}</td>
                            <td>{formatNumber(pair[`${side}_hairpin`], 1)}</td>
                            <td>{formatNumber(pair[`${side}_3stab`], 1)}</td>
                            <td>{formatNumber(pair.penalty, 3)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Primer3PlusPage;

